// ─────────────────────────────────────────────────────────────────────────────
//  lib/support/supportService.js
//  Client-side service — reads/manages support tickets.
// ─────────────────────────────────────────────────────────────────────────────

import { API_BASE_URL } from '@/_config/env';

// ─── Auth helper ──────────────────────────────────────────────────────────────

const getAuthToken = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authToken=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
};

const authHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: 'Bearer ' + token } : {};
};

// ─── Fetch helpers ────────────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
    const res = await fetch(url, options);
    let json;
    try { json = await res.json(); } catch { json = {}; }
    if (!res.ok) {
        return { success: false, message: json?.message || `Request failed (${res.status})` };
    }
    return { success: true, data: json?.data ?? json, message: json?.message, pagination: json?.pagination };
}

// ─── Active endpoints ─────────────────────────────────────────────────────────

/**
 * Fetch all tickets for the logged-in user.
 * @param {{ status?: string, page?: number, limit?: number }} params
 */
export async function fetchMyTickets({ status, page = 1, limit = 10 } = {}) {
    const params = new URLSearchParams();
    if (status && status !== 'ALL') params.set('status', status);
    params.set('page', String(page));
    params.set('limit', String(limit));

    // Route through the Next.js proxy so the httpOnly authToken cookie is readable server-side
    const url = `/api/support/my-tickets?${params.toString()}`;
    return apiFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });
}

/**
 * Fetch a single ticket by order ID.
 * @param {string} orderId
 */
export async function fetchTicketByOrder(orderId) {
    const url = `/api/support/ticket/order/${encodeURIComponent(orderId)}`;
    return apiFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });
}

/**
 * Fetch a single ticket with full reply thread.
 * GET /support/ticket/:ticketNumber
 * @param {string} ticketNumber
 */
export async function fetchSingleTicket(ticketNumber) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketNumber)}`;
    return apiFetch(url, { method: 'GET' });
}

/**
 * Fetch ticket status by ticket ID (public endpoint — no auth needed).
 * @param {string} ticketId
 */
export async function fetchTicketStatus(ticketId) {
    const url = `${API_BASE_URL}/support/status?ticketId=${encodeURIComponent(ticketId)}`;
    return apiFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });
}

// ─── Placeholder endpoints (coming soon) ──────────────────────────────────────

/**
 * Reply to a ticket.
 * POST /support/ticket/:ticketNumber/reply  (multipart/form-data, images optional, max 3)
 * @param {string}   ticketNumber
 * @param {string}   message
 * @param {File[]}   images
 */
export async function replyToTicket(ticketNumber, message, images = []) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketNumber)}/reply`;
    const fd  = new FormData();
    fd.append('message', message);
    images.forEach(img => fd.append('images', img));
    return apiFetch(url, { method: 'POST', body: fd });
}

/**
 * Close a ticket.
 * @param {string} ticketId
 */
export async function closeTicket(ticketId) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketId)}/status`;
    return apiFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'CLOSED' }),
    });
}

/**
 * Mark a ticket as resolved.
 * @param {string} ticketId
 */
export async function resolveTicket(ticketId) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketId)}/resolve`;
    return apiFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
    });
}

/**
 * Reopen a resolved ticket.
 * POST /support/ticket/:ticketNumber/reopen  (multipart/form-data, images optional, max 3)
 * @param {string}   ticketNumber
 * @param {string}   comment       reason for reopening
 * @param {File[]}   images
 */
export async function reopenTicket(ticketNumber, comment, images = []) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketNumber)}/reopen`;
    const fd  = new FormData();
    fd.append('comment', comment);
    images.forEach(img => fd.append('images', img));
    return apiFetch(url, { method: 'POST', body: fd });
}

/**
 * Approve the pending replacement/repair cost proposal on a ticket.
 * POST /support/ticket/:ticketNumber/cost-proposal/approve
 * @param {string} ticketNumber
 */
export async function approveCostProposal(ticketNumber) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketNumber)}/cost-proposal/approve`;
    return apiFetch(url, { method: 'POST' });
}

/**
 * Reject the pending replacement/repair cost proposal on a ticket.
 * POST /support/ticket/:ticketNumber/cost-proposal/reject
 * @param {string} ticketNumber
 * @param {string} [note]  optional reason for rejecting
 */
export async function rejectCostProposal(ticketNumber, note = '') {
    const url = `/api/support/ticket/${encodeURIComponent(ticketNumber)}/cost-proposal/reject`;
    return apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
    });
}

/**
 * Submit feedback for a resolved ticket.
 * POST /support/ticket/:ticketNumber/feedback
 * @param {string} ticketNumber
 * @param {number} rating   1–5
 * @param {string} comment
 */
export async function submitFeedback(ticketNumber, rating, comment) {
    const url = `/api/support/ticket/${encodeURIComponent(ticketNumber)}/feedback`;
    return apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
    });
}
